import type { FileDiffHunk } from "@/lib/chat";

/** 承認カードは実行前に読ませるものなので、長いファイル作成は先頭だけにする。 */
const MAX_PREVIEW_LINES = 200;

export interface EditInputDiff {
	hunks: FileDiffHunk[];
	/** 置換は実行前だとファイルのどこに当たるか分からないため false。 */
	lineNumbers: boolean;
	truncated: boolean;
	/** 先頭だけにする前の行数。 */
	added: number;
	removed: number;
}

function prefixLines(prefix: "+" | "-", text: string): string[] {
	if (text === "") return [];
	return text
		.replace(/\n$/, "")
		.split("\n")
		.map((line) => prefix + line);
}

function insertLine(value: unknown): number | null {
	const line = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
	return typeof line === "number" && Number.isInteger(line) && line >= 0 ? line : null;
}

/**
 * 先頭だけにするときも、置き換え後の行が見えるよう削除と追加の両方から残す。
 * 削除を途中で切ったときは、切れ目が分かるよう追加を別の塊にする。
 */
function previewLineGroups(removed: string[], added: string[]): string[][] {
	const keepRemoved = Math.min(
		removed.length,
		Math.max(MAX_PREVIEW_LINES / 2, MAX_PREVIEW_LINES - added.length),
	);
	const shownRemoved = removed.slice(0, keepRemoved);
	const shownAdded = added.slice(0, MAX_PREVIEW_LINES - keepRemoved);
	if (shownRemoved.length < removed.length && shownAdded.length > 0) {
		return [shownRemoved, shownAdded];
	}
	return [[...shownRemoved, ...shownAdded]];
}

/**
 * ファイル編集ツールの入力から、実行前に見せる差分を作る。ファイルの中身は読まず、
 * 入力に書かれた追加・削除の行だけを出す。表示できない入力や変更のない入力では null。
 */
export function editInputDiff(input: Record<string, unknown>): EditInputDiff | null {
	let removed: string[] = [];
	let added: string[];
	let oldStart: number;
	let newStart: number;
	let lineNumbers = true;
	if (input.command === "create" && typeof input.file_text === "string") {
		added = prefixLines("+", input.file_text);
		oldStart = 0;
		newStart = 1;
	} else if (input.command === "insert" && typeof input.insert_text === "string") {
		const after = insertLine(input.insert_line);
		if (after === null) return null;
		added = prefixLines("+", input.insert_text);
		oldStart = after;
		newStart = after + 1;
	} else if (input.command === "str_replace" && typeof input.old_str === "string") {
		removed = prefixLines("-", input.old_str);
		added = typeof input.new_str === "string" ? prefixLines("+", input.new_str) : [];
		oldStart = 1;
		newStart = 1;
		lineNumbers = false;
	} else {
		return null;
	}
	if (removed.length + added.length === 0) return null;

	const groups = previewLineGroups(removed, added);
	return {
		hunks: groups.map((lines) => ({
			oldStart,
			oldLines: removed.length,
			newStart,
			newLines: added.length,
			lines,
		})),
		lineNumbers,
		truncated: groups.flat().length < removed.length + added.length,
		added: added.length,
		removed: removed.length,
	};
}
